/**
 * Generic utilities — v.1-D §7.6.
 *
 * - `cn`: classnames-style join that filters falsy entries.
 * - `formatDuration`: humanises a millisecond span ("1h 2m 3s").
 * - `formatRelativeTime`: humanises an epoch millisecond relative to now.
 * - `channelThumbGradient`: deterministic gradient seeded from a channel ID
 *   (no `Math.random` — repeated calls with the same ID return identical CSS).
 *
 * Dev-mode `window.__*` exposures (fab #5 fix per §0.4): when
 * `import.meta.env.DEV` is true and a real `window` exists, two helpers are
 * attached to `window` for the Playwright smoke suite to assert against.
 * Production builds drop the entire `if` block via tree-shaking.
 */
export function cn(
  ...classes: (string | undefined | null | false)[]
): string {
  return classes.filter(Boolean).join(' ');
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function formatRelativeTime(epochMs: number): string {
  const diffMs = Date.now() - epochMs;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

export function channelThumbGradient(channelId: string): string {
  const suffix = channelId.slice(-4);
  const seed = (parseInt(suffix, 36) || 0) % 360;
  const hueA = seed;
  const hueB = (seed + 120) % 360;
  return `linear-gradient(135deg, hsl(${hueA}, 45%, 18%), hsl(${hueB}, 55%, 35%))`;
}

// Dev-mode exposure for smoke-suite assertions. Gated by `import.meta.env.DEV`
// so the production bundle drops the block entirely. The additional `window`
// guard prevents a `ReferenceError` when this module loads under vitest's
// default node environment (no DOM).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__channelThumbGradient =
    channelThumbGradient;
  (window as unknown as Record<string, unknown>).__formatRelativeTime =
    formatRelativeTime;
}
